import { useEffect } from 'react';
import { Modal, TextInput, Textarea, Button, Stack, Group } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { personSchema, type PersonFormValues } from '../schemas/personSchema';
import type { Person } from '@/types/models';

interface PersonFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: PersonFormValues, keepOpen?: boolean) => void;
  person?: Person | null;
  isLoading?: boolean;
}

export function PersonForm({ opened, onClose, onSubmit, person, isLoading }: PersonFormProps) {
  const form = useForm<PersonFormValues>({
    validate: zodResolver(personSchema),
    initialValues: {
      full_name: '',
      email: '',
      employee_id: '',
      title: '',
      phone: '',
      notes: '',
      department_id: null,
      location_id: null,
    },
  });

  // Update form when person changes (for editing)
  useEffect(() => {
    if (person) {
      form.setValues({
        full_name: person.full_name,
        email: person.email || '',
        employee_id: person.employee_id || '',
        title: person.title || '',
        phone: person.phone || '',
        notes: person.notes || '',
        department_id: person.department_id,
        location_id: person.location_id,
      });
    } else {
      form.reset();
    }
  }, [person]);

  const handleSubmit = (values: PersonFormValues, keepOpen = false) => {
    // Convert empty strings to null for optional fields
    const cleanedValues = {
      ...values,
      email: values.email || null,
      employee_id: values.employee_id || null,
      title: values.title || null,
      phone: values.phone || null,
      notes: values.notes || null,
    };
    onSubmit(cleanedValues, keepOpen);
    if (keepOpen) {
      form.reset();
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={person ? 'Edit Person' : 'Add New Person'}
      size="lg"
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        onKeyDown={(e) => {
          // Allow Enter to submit form from any field (except textareas)
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
            e.preventDefault();
            form.onSubmit(handleSubmit)();
          }
        }}
      >
        <Stack gap="md">
          <TextInput
            label="Full Name"
            placeholder="John Doe"
            required
            {...form.getInputProps('full_name')}
          />

          <Group grow>
            <TextInput
              label="Email"
              placeholder="john.doe@company.com"
              type="email"
              {...form.getInputProps('email')}
            />

            <TextInput
              label="Phone"
              placeholder="+1 (555) 123-4567"
              {...form.getInputProps('phone')}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Employee ID"
              placeholder="EMP-001"
              {...form.getInputProps('employee_id')}
            />

            <TextInput
              label="Title"
              placeholder="Software Engineer"
              {...form.getInputProps('title')}
            />
          </Group>

          {/* TODO: Add Department and Location dropdowns when those entities are loaded */}

          <Textarea
            label="Notes"
            placeholder="Additional information..."
            rows={3}
            {...form.getInputProps('notes')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            {!person && (
              <Button
                type="button"
                variant="light"
                onClick={() => form.onSubmit((values) => handleSubmit(values, true))()}
                loading={isLoading}
              >
                Create & Add Another
              </Button>
            )}
            <Button
              type="button"
              onClick={() => form.onSubmit((values) => handleSubmit(values, false))()}
              loading={isLoading}
            >
              {person ? 'Update Person' : 'Create Person'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
